var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@tanstack/react-query';
import { SUPPORTBFF_URL } from '@zapier/product-get-help/esm/constants';
import { useMemo } from 'react';
import { QUERY_KEY, SUPPORT_BFF_ENDPOINT } from '../constants';
const HOME_FOLDER = {
    id: '0',
    isShared: false,
    title: 'Home',
};
const DEFAULT_FOLDER_IDS = [HOME_FOLDER.id];
const FOLDER_GROUPS = [
    { title: 'Private Folders', isShared: false },
    {
        title: 'Shared Folders',
        isShared: true,
    },
];
const zapCompare = (first, second) => (first.title || '').localeCompare(second.title || '');
const filterZaps = (zaps, search) => {
    if (!search) {
        return zaps;
    }
    const searchLowerCase = search.toLowerCase();
    return zaps.filter(({ title }) => title && title.toLowerCase().includes(searchLowerCase));
};
const buildZapsByFolder = (data, search) => {
    // Null values from supportbff are allowed.
    // And for some reason `?.` breaks flow type generation
    const zaps = ((data && data.zaps && data.zaps.filter(zap => !!zap)) ||
        []);
    const sortedZaps = zaps.sort(zapCompare);
    const filteredZaps = filterZaps(sortedZaps, search);
    const zapsByFolder = filteredZaps.reduce((map, zap) => {
        const folderIds = zap.folderIds.length === 0 ? DEFAULT_FOLDER_IDS : zap.folderIds;
        folderIds.forEach(folderId => {
            const folderZaps = map.get(folderId);
            if (folderZaps) {
                folderZaps.push(zap);
            }
            else {
                map.set(folderId, [zap]);
            }
        });
        return map;
    }, new Map());
    return zapsByFolder;
};
const folderCompare = (first, second) => {
    // Home comes first
    if (first.id === '0') {
        return -1;
    }
    else if (second.id === '0') {
        return 1;
    }
    // Sort by title
    return first.title.localeCompare(second.title);
};
const buildFolders = (data) => {
    // For some reason `?.` breaks flow type generation
    const folders = (data && data.account && data.account.folders) || [];
    const foldersWithHome = [HOME_FOLDER, ...folders];
    return foldersWithHome.sort(folderCompare);
};
const buildFoldersWithZaps = (folders, zapsByFolder, shouldBeShared, kindsToReturn) => folders.reduce((result, { id, isShared, title }) => {
    if (shouldBeShared !== isShared) {
        return result;
    }
    const zaps = zapsByFolder.get(id) || [];
    const zapsFilteredByKind = zaps.filter(zap => zap.kind !== null && kindsToReturn.includes(zap.kind));
    if (zapsFilteredByKind.length === 0) {
        return result;
    }
    result.push({
        id,
        title,
        zaps: zapsFilteredByKind,
    });
    return result;
}, []);
export const buildData = (data, search, kind) => {
    const zapsByFolder = buildZapsByFolder(data, search);
    const sortedFolders = buildFolders(data);
    return FOLDER_GROUPS.reduce((folderGroups, { title, isShared }) => {
        const foldersWithZaps = buildFoldersWithZaps(sortedFolders, zapsByFolder, isShared, kind);
        if (foldersWithZaps.length > 0) {
            folderGroups.push({
                title,
                folders: foldersWithZaps,
            });
        }
        return folderGroups;
    }, []);
};
const fetchZaps = (accountId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield fetch(`${SUPPORTBFF_URL}${SUPPORT_BFF_ENDPOINT}/?accountId=${accountId}`, {
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error('Failed to fetch Zaps from supportbff.');
        }
        return { zapData: yield response.json(), error: null };
    }
    catch (error) {
        let message = '';
        if (error instanceof Error) {
            message = error.message;
        }
        return { zapData: null, error: message };
    }
});
const useData = ({ accountId, search, kind }) => {
    const { error, data, isLoading } = useQuery({
        queryKey: [QUERY_KEY, accountId],
        queryFn: () => fetchZaps(accountId),
        enabled: !!accountId,
        staleTime: 60000, // 60 seconds
    });
    const processedData = useMemo(() => {
        if (error || isLoading || !data || !data.zapData) {
            return [];
        }
        return buildData(data.zapData, search, kind);
    }, [error, isLoading, data, search, kind]);
    return {
        error,
        isLoading,
        data: processedData,
    };
};
export default useData;
